import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { TrendingUp, DollarSign, Target, ArrowRight, Zap, Wind, Battery } from 'lucide-react';
import { useWallet } from '../context/SimpleWalletContext';
import { useFirebaseAuth } from '../context/FirebaseAuthContext';

interface Investment {
  id: number;
  projectId: number;
  projectName: string;
  solAmount: number;
  usdAmount: number;
  investmentDate: string;
  expectedROI: string;
  currentValue: number;
  profit: number;
  status: 'active' | 'completed';
}

export default function MyInvestments() {
  const { user } = useFirebaseAuth();
  const { balance, getUserInvestments } = useWallet();
  const [investments, setInvestments] = useState<Investment[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadInvestments();
  }, []);

  const loadInvestments = async () => {
    setLoading(true);
    await new Promise(resolve => setTimeout(resolve, 1000));

    if (user) {
      // Get current user email
      const userEmail = localStorage.getItem('userEmail') || user.email || 'demo@example.com';

      // Get real investments from localStorage
      const userInvestments = getUserInvestments(userEmail);

      // Convert to display format with ROI calculations
      const investmentsWithROI: Investment[] = userInvestments.map(inv => {
        const expectedROI = inv.projectName === 'SOLAR_ENERGY' ? '24.5%' :
          inv.projectName === 'WIND_ENERGY' ? '26.8%' : '28.2%';
        const roiMultiplier = parseFloat(expectedROI) / 100;
        const currentValue = inv.usdAmount * (1 + roiMultiplier);
        const profit = currentValue - inv.usdAmount;

        return {
          id: inv.id,
          projectId: inv.projectId,
          projectName: inv.projectName,
          solAmount: inv.solAmount,
          usdAmount: inv.usdAmount,
          investmentDate: inv.investmentDate,
          expectedROI: expectedROI,
          currentValue: currentValue,
          profit: profit,
          status: 'active' as const
        };
      });

      setInvestments(investmentsWithROI);
    } else {
      // For non-logged-in users, show empty investments
      setInvestments([]);
    }

    setLoading(false);
  };

  const getProjectIcon = (projectName: string) => {
    switch (projectName) {
      case 'SOLAR_ENERGY': return <Zap className="w-5 h-5" style={{ color: 'var(--neon-green)' }} />;
      case 'WIND_ENERGY': return <Wind className="w-5 h-5" style={{ color: 'var(--cyber-blue)' }} />;
      case 'ENERGY_STORAGE': return <Battery className="w-5 h-5" style={{ color: 'var(--electric-purple)' }} />;
      default: return <Target className="w-5 h-5" style={{ color: 'var(--neon-green)' }} />;
    }
  };

  const totalInvested = investments.reduce((sum, inv) => sum + inv.usdAmount, 0);
  const totalCurrentValue = investments.reduce((sum, inv) => sum + inv.currentValue, 0);
  const totalProfit = investments.reduce((sum, inv) => sum + inv.profit, 0);
  const totalSOLInvested = investments.reduce((sum, inv) => sum + inv.solAmount, 0);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
          <p className="mt-2 text-gray-600">Loading your investments...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
      <div className="px-4 py-6 sm:px-0">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            My Investments
          </h1>
          <p className="mt-2 text-gray-600">
            Track your clean energy investments and returns
          </p>
        </div>

        {/* Investment Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <motion.div
            className="bg-white overflow-hidden shadow rounded-lg"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-white" />
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Total Invested
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      ${totalInvested.toLocaleString()}
                    </dd>
                    <dd className="text-sm text-gray-500">
                      {totalSOLInvested} SOL
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="bg-white overflow-hidden shadow rounded-lg"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-white" />
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Current Value
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      ${totalCurrentValue.toLocaleString()}
                    </dd>
                    <dd className="text-sm text-gray-500">
                      {((totalCurrentValue / totalInvested - 1) * 100).toFixed(1)}% gain
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="bg-white overflow-hidden shadow rounded-lg"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-green-600 rounded-md flex items-center justify-center">
                    <span className="text-white text-sm font-medium">+</span>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Total Profit
                    </dt>
                    <dd className="text-lg font-medium text-green-600">
                      +${totalProfit.toLocaleString()}
                    </dd>
                    <dd className="text-sm text-gray-500">
                      {investments.length} investments
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="bg-white overflow-hidden shadow rounded-lg"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-purple-500 rounded-md flex items-center justify-center">
                    <Target className="w-5 h-5 text-white" />
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Active Projects
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {investments.filter(inv => inv.status === 'active').length}
                    </dd>
                    <dd className="text-sm text-gray-500">
                      {balance.toFixed(1)} SOL available
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Investments List */}
        {investments.length === 0 ? (
          <motion.div
            className="bg-white shadow rounded-lg p-12 text-center"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5 }}
          >
            <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
              <Target className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Investments Yet</h3>
            <p className="text-gray-500 mb-6">Start your clean energy investment journey today!</p>
            <Link
              to="/projects"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 transition-colors"
            >
              Browse Projects
              <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </motion.div>
        ) : (
          <motion.div
            className="bg-white shadow rounded-lg overflow-hidden"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Your Investment Portfolio
              </h3>

              <div className="space-y-4">
                {investments.map((investment, index) => (
                  <motion.div
                    key={investment.id}
                    className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.7 + index * 0.1 }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                          {getProjectIcon(investment.projectName)}
                        </div>
                        <div>
                          <h4 className="text-sm font-medium text-gray-900">
                            {investment.projectName}
                          </h4>
                          <p className="text-sm text-gray-500">
                            Invested on {new Date(investment.investmentDate).toLocaleDateString()}
                          </p>
                        </div>
                      </div>

                      <div className="text-right">
                        <div className="text-sm font-medium text-gray-900">
                          {investment.solAmount} SOL
                        </div>
                        <div className="text-sm text-gray-500">
                          ${investment.usdAmount.toLocaleString()}
                        </div>
                      </div>

                      <div className="text-right">
                        <div className="text-sm font-medium text-gray-900">
                          ${investment.currentValue.toLocaleString()}
                        </div>
                        <div className="text-sm text-green-600">
                          +${investment.profit.toLocaleString()}
                        </div>
                      </div>

                      <div className="text-right">
                        <div className="text-sm font-medium text-gray-900">
                          {investment.expectedROI}
                        </div>
                        <div className="text-sm text-gray-500">
                          ROI
                        </div>
                      </div>

                      <div className="text-right">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${investment.status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-blue-100 text-blue-800'
                          }`}>
                          {investment.status}
                        </span>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200">
                <Link
                  to="/projects"
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 transition-colors"
                >
                  Invest in More Projects
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
